import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { State } from "../reducer";
import { getSettings, AppSettings } from "../api/settings.api";
import { ikThumb } from "../utils/image";

const DISMISS_KEY = "woxa.fridaySpecial.dismissed";

// Bangkok-local YYYY-MM-DD — schedule + cron run on Asia/Bangkok, so we
// align the modal's "today" with the same wall-clock to avoid edge cases
// around midnight in other timezones.
const bangkokDateKey = (d: Date) =>
  d.toLocaleDateString("en-CA", { timeZone: "Asia/Bangkok" });

const isFridayBangkok = (d: Date) =>
  d.toLocaleDateString("en-US", { timeZone: "Asia/Bangkok", weekday: "short" }) === "Fri";

/**
 * Friday-only welcome modal that announces the special menu.
 *
 * Visibility (all must hold):
 *  - Today is Friday in Bangkok
 *  - The user is signed in (any role)
 *  - Banner is `active=true` in app_settings and has at least image or description
 *  - User hasn't tapped "Don't show again" earlier today
 */
const FridaySpecialModal: React.FC = () => {
  const role = useSelector((s: State) => s.user?.role);
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<AppSettings | null>(null);

  useEffect(() => {
    if (!role) return;

    const today = new Date();
    if (!isFridayBangkok(today)) return;

    const todayKey = bangkokDateKey(today);
    try {
      if (localStorage.getItem(DISMISS_KEY) === todayKey) return;
    } catch { /* private mode — show anyway */ }

    let cancelled = false;
    getSettings()
      .then((s) => {
        if (cancelled) return;
        if (!s.fridaySpecialActive) return;
        if (!s.fridaySpecialDescription && !s.fridaySpecialImage) return;
        setSettings(s);
        setOpen(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [role]);

  if (!open || !settings) return null;

  const close = () => setOpen(false);
  const dismissForToday = () => {
    try {
      localStorage.setItem(DISMISS_KEY, bangkokDateKey(new Date()));
    } catch { /* ignore */ }
    close();
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-ink-1000/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 animate-fade-in"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="friday-special-title"
    >
      {/* Card — fixed shape with proportional regions:
            ~40% hero image · header · ~20% body (scroll) · footer
          The flex-basis + min-h-0 trick ensures regions stay roughly in
          ratio while still letting long descriptions scroll inside. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-3xl shadow-floaty w-full max-w-[360px] flex flex-col overflow-hidden animate-slide-up max-h-[80vh] max-h-[80dvh]"
      >
        {/* === HERO IMAGE — ~40% =================================== */}
        <div className="relative shrink-0 w-full basis-[40%] min-h-[180px] bg-ink-100">
          {settings.fridaySpecialImage ? (
            <img
              src={ikThumb(settings.fridaySpecialImage, 720)}
              alt="Friday special"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400 via-orange-400 to-rose-500 flex items-center justify-center">
              <span className="text-6xl drop-shadow-lg" aria-hidden>✨</span>
            </div>
          )}
          {/* Subtle bottom gradient — gives the close-button + ribbon contrast */}
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/35 to-transparent pointer-events-none" />
          {/* Festive ribbon */}
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 h-6 rounded-full bg-amber-500 text-white text-[10px] font-black tracking-wider uppercase shadow-soft">
            <span className="text-[12px] leading-none" aria-hidden>✨</span>
            Friday Only
          </span>
          {/* Close */}
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-ink-1000 hover:bg-white shadow-soft active:scale-95 transition"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* === HEADER ============================================== */}
        <header className="shrink-0 px-5 pt-4 pb-2 flex items-center gap-2">
          <span
            className="block w-1 h-5 rounded-full"
            style={{ background: "linear-gradient(180deg, #F59E0B, #EF4444)" }}
            aria-hidden
          />
          <h2
            id="friday-special-title"
            className="font-display font-black text-[19px] leading-tight tracking-tightest text-ink-1000"
          >
            This Friday's Special
          </h2>
        </header>

        {/* === BODY — ~20%, scrollable ============================= */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-3 basis-[20%]">
          {settings.fridaySpecialDescription ? (
            <p className="text-[13px] text-ink-700 leading-relaxed whitespace-pre-line break-words">
              {settings.fridaySpecialDescription}
            </p>
          ) : (
            <p className="text-[13px] text-ink-400 italic">No description yet.</p>
          )}
        </div>

        {/* === FOOTER ============================================== */}
        <footer
          className="shrink-0 bg-ink-50 px-4 pt-3 flex gap-2 border-t border-ink-100"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <button
            type="button"
            onClick={dismissForToday}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-white text-ink-700 text-xs font-semibold ring-1 ring-inset ring-ink-200 hover:bg-ink-100 active:scale-[0.97] transition"
          >
            Don't show again
          </button>
          <button
            type="button"
            onClick={close}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-ink-1000 text-ink-0 text-xs font-bold shadow-soft hover:bg-ink-900 active:scale-[0.97] transition"
          >
            Got it
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </footer>
      </div>
    </div>
  );
};

export default FridaySpecialModal;
